// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import type { PromptSectionProps } from './index';

interface PromptCategory {
  id: number;
  category_id: string;
  name_en: string;
  name_ru: string;
  description_en?: string;
  description_ru?: string;
  content_type: 'image' | 'video' | null;
  mode: 'default' | 'characters';
  is_active: boolean;
}

export default function PromptSection({
  workflowId,
  language,
  selectedCategoryIds,
  onCategoryIdsChange,
  maxReferenceImages,
  onMaxReferenceImagesChange,
  maxReferenceVideos,
  onMaxReferenceVideosChange,
  maxReferenceAudio,
  onMaxReferenceAudioChange
}: PromptSectionProps) {
  const { getToken } = useAuth();
  const t = useLocalization(language);

  const [allCategories, setAllCategories] = useState<PromptCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCategories();
  }, [workflowId]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken({ template: 'withmail' });

      // Load all root categories (parent_id is null)
      const categoriesResponse = await fetch('/api/admin/prompt-categories?parent_id=null', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!categoriesResponse.ok) {
        throw new Error('Failed to load categories');
      }

      const categoriesData = await categoriesResponse.json();
      setAllCategories(categoriesData.categories || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError(error instanceof Error ? error.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCategory = (categoryId: number) => {
    const newIds = selectedCategoryIds.includes(categoryId)
      ? selectedCategoryIds.filter(id => id !== categoryId)
      : [...selectedCategoryIds, categoryId];
    onCategoryIdsChange(newIds);
  };

  const getCategoryName = (category: PromptCategory) => {
    return language === 'ru' ? category.name_ru : category.name_en;
  };

  const getCategoryDescription = (category: PromptCategory) => {
    return language === 'ru' ? category.description_ru : category.description_en;
  };

  const getModeLabel = (mode: string) => {
    if (mode === 'characters') {
      return language === 'ru' ? 'Персонажи' : 'Characters';
    }
    return language === 'ru' ? 'Стандартный' : 'Default';
  };

  const getContentTypeLabel = (contentType: string | null) => {
    if (contentType === 'image') {
      return language === 'ru' ? 'Изображения' : 'Image';
    }
    if (contentType === 'video') {
      return language === 'ru' ? 'Видео' : 'Video';
    }
    return '';
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icon name={ICONS.PROMPTS} size={20} className="text-purple-400" />
            {language === 'ru' ? 'Категории референсов' : 'Reference Categories'}
          </h3>
        </div>
        <p className="text-sm text-gray-400">
          {language === 'ru'
            ? 'Выберите категории референсов, которые будут доступны в этом workflow'
            : 'Select reference categories that will be available in this workflow'
          }
        </p>
      </section>

      {/* Reference Media Limits */}
      <section className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/50 space-y-4">
        {/* Max Reference Images */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {language === 'ru' ? 'Макс. референс изображений' : 'Max Reference Images'}
            </label>
            <p className="text-xs text-gray-500">
              {language === 'ru'
                ? 'Максимальное количество изображений в Reference Builder (0 = отключено)'
                : 'Maximum images that can be selected in Reference Builder (0 = disabled)'
              }
            </p>
          </div>
          <div className="w-24">
            <input
              type="number"
              min={0}
              max={50}
              value={maxReferenceImages ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  onMaxReferenceImagesChange(null);
                } else {
                  const num = parseInt(value, 10);
                  if (!isNaN(num) && num >= 0 && num <= 50) {
                    onMaxReferenceImagesChange(num);
                  }
                }
              }}
              placeholder="10"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
            />
          </div>
        </div>

        {/* Max Reference Videos */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {language === 'ru' ? 'Макс. референс видео' : 'Max Reference Videos'}
            </label>
            <p className="text-xs text-gray-500">
              {language === 'ru'
                ? 'Максимальное количество видео в Reference Builder (0 = отключено)'
                : 'Maximum videos that can be selected in Reference Builder (0 = disabled)'
              }
            </p>
          </div>
          <div className="w-24">
            <input
              type="number"
              min={0}
              max={50}
              value={maxReferenceVideos ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  onMaxReferenceVideosChange(null);
                } else {
                  const num = parseInt(value, 10);
                  if (!isNaN(num) && num >= 0 && num <= 50) {
                    onMaxReferenceVideosChange(num);
                  }
                }
              }}
              placeholder="0"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
            />
          </div>
        </div>

        {/* Max Reference Audio */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              {language === 'ru' ? 'Макс. референс аудио' : 'Max Reference Audio'}
            </label>
            <p className="text-xs text-gray-500">
              {language === 'ru'
                ? 'Максимальное количество аудио в Reference Builder (0 = отключено)'
                : 'Maximum audio files that can be selected in Reference Builder (0 = disabled)'
              }
            </p>
          </div>
          <div className="w-24">
            <input
              type="number"
              min={0}
              max={50}
              value={maxReferenceAudio ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  onMaxReferenceAudioChange(null);
                } else {
                  const num = parseInt(value, 10);
                  if (!isNaN(num) && num >= 0 && num <= 50) {
                    onMaxReferenceAudioChange(num);
                  }
                }
              }}
              placeholder="0"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
            />
          </div>
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Categories List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
        </div>
      ) : allCategories.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700/50">
          <Icon name={ICONS.PROMPTS} size={48} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">
            {language === 'ru' ? 'Категории не найдены' : 'No categories found'}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {language === 'ru'
              ? 'Создайте категории в разделе "Категории промптов"'
              : 'Create categories in the "Prompt Categories" section'
            }
          </p>
        </div>
      ) : (
        <section className="space-y-4">
          {/* Categories Grid */}
          <div className="grid grid-cols-1 gap-3">
            {allCategories.map((category) => (
              <div
                key={category.id}
                onClick={() => handleToggleCategory(category.id)}
                className={`bg-gray-800/50 border rounded-lg p-4 transition-all cursor-pointer ${
                  selectedCategoryIds.includes(category.id)
                    ? 'border-purple-500 bg-purple-600/10'
                    : 'border-gray-600/50 hover:border-gray-500'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div className="flex-shrink-0 mt-1">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedCategoryIds.includes(category.id)
                        ? 'bg-purple-600 border-purple-600'
                        : 'border-gray-500 hover:border-gray-400'
                    }`}>
                      {selectedCategoryIds.includes(category.id) && (
                        <Icon name={ICONS.CHECK} size={14} className="text-white" />
                      )}
                    </div>
                  </div>

                  {/* Category Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-medium">
                        {getCategoryName(category)}
                      </h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        category.mode === 'characters'
                          ? 'bg-orange-900/30 text-orange-300 border border-orange-500/30'
                          : 'bg-blue-900/30 text-blue-300 border border-blue-500/30'
                      }`}>
                        {getModeLabel(category.mode)}
                      </span>
                      {category.content_type && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-700/50 text-gray-300 border border-gray-600/50">
                          {getContentTypeLabel(category.content_type)}
                        </span>
                      )}
                    </div>
                    {getCategoryDescription(category) && (
                      <p className="text-sm text-gray-400">
                        {getCategoryDescription(category)}
                      </p>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      ID: {category.category_id}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Selected count */}
          <div className="pt-4 border-t border-gray-700/50">
            <span className="text-sm text-gray-400">
              {selectedCategoryIds.length === 0
                ? (language === 'ru' ? 'Категории не выбраны' : 'No categories selected')
                : language === 'ru'
                  ? `Выбрано: ${selectedCategoryIds.length}`
                  : `${selectedCategoryIds.length} selected`
              }
            </span>
          </div>
        </section>
      )}
    </div>
  );
}
